import { useState } from 'react'
import {
  Button,
  Card,
  Divider,
  Group,
  Select,
  TextInput,
  Title,
  Stack,
  ActionIcon,
  Text,
  Alert,
  InputBase,
} from '@mantine/core'
import { IMaskInput } from 'react-imask'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { IconPlus, IconTrash, IconInfoCircle } from '@tabler/icons-react'
import { useMedplum } from '@medplum/react'
import { Organization } from '@medplum/fhirtypes'
import { states } from '@/utils/states.ts'
import { logError } from '@/utils/errorUtils'
import { LAB_ACCOUNT_SYSTEMS } from '@/pages/Patient/components/OrderTests/formData'
import { LAB_ACCOUNT_LABELS } from './labAccountLabels'
import { EIN_IDENTIFIER_SYSTEM, NPI_IDENTIFIER_SYSTEM } from '@/model/middleware-types'
import { useClient } from '@/hooks/useClient'
import type { InsuranceCategory, InsurancePlanType, USStateCode } from '@behuman/core-contract'
import {
  PlatformAgreements,
  type AgreementFormRow,
  type AgreementsHostForm,
} from '@/pages/Super/components/PlatformAgreements'
import {
  OrganizationStripeSection,
  type StripeHostForm,
  type StripeKeysFormRow,
} from '@/pages/Super/components/OrganizationStripeSection'
import {
  OrganizationDoseSpotSection,
  type DoseSpotClinicFormRow,
  type DoseSpotHostForm,
} from '@/pages/Super/components/OrganizationDoseSpotSection'
import {
  OrganizationBridgeSection,
  type BridgeHostForm,
} from '@/pages/Super/components/OrganizationBridgeSection'
import {
  OrganizationStediSection,
  type StediHostForm,
} from '@/pages/Super/components/OrganizationStediSection'

interface CreateUpdateOrganizationProps {
  organization?: Organization
  code: string
  onSave?: (organization: Organization) => void
  onCancel?: () => void
}

interface OrganizationFormData {
  name: string
  type: string
  aliases: string[]
  phone: string
  faxPhone: string
  billingNpi: string
  billingTaxId: string
  address: {
    state: string
    zipCode: string
    city: string
    address: string
  }
  projectId?: string
  labAccounts: Record<string, string>
  agreements: AgreementFormRow[]
  stripeKeys: StripeKeysFormRow
  doseSpotClinic: DoseSpotClinicFormRow
  bridgeApiKey: string
  bridgeWebhookSecret: string
  stediApiKey: string
}

// Organization type code system mapping
const ORGANIZATION_TYPES = {
  prov: 'Healthcare Provider',
  dept: 'Hospital Department',
  team: 'Organizational team',
  govt: 'Government',
  ins: 'Insurance Company',
  pay: 'Payer',
  edu: 'Educational Institute',
  reli: 'Religious Institution',
  crs: 'Clinical Research Sponsor',
  cg: 'Community Group',
  bus: 'Non-Healthcare Business or Corporation',
  ntwk: 'Network',
}

export function CreateUpdateOrganization({
  organization,
  code,
  onSave,
  onCancel,
}: CreateUpdateOrganizationProps) {
  const medplum = useMedplum()
  const middleware = useClient()
  const [isLoading, setIsLoading] = useState(false)

  const initialLabAccounts: Record<string, string> = {}
  for (const [key, system] of Object.entries(LAB_ACCOUNT_SYSTEMS)) {
    initialLabAccounts[key] =
      organization?.identifier?.find((id) => id.system === system)?.value || ''
  }

  const form = useForm<OrganizationFormData>({
    initialValues: {
      name: organization?.name || '',
      type: organization?.type?.[0]?.coding?.[0]?.code || code || '',
      aliases: organization?.alias || [''],
      phone: organization?.telecom?.find((t) => t.system === 'phone')?.value || '',
      faxPhone: organization?.telecom?.find((t) => t.system === 'fax')?.value || '',
      billingNpi:
        organization?.identifier?.find((id) => id.system === NPI_IDENTIFIER_SYSTEM)?.value || '',
      billingTaxId:
        organization?.identifier?.find((id) => id.system === EIN_IDENTIFIER_SYSTEM)?.value || '',
      address: {
        state: organization?.address?.[0]?.state || '',
        zipCode: organization?.address?.[0]?.postalCode || '',
        city: organization?.address?.[0]?.city || '',
        address: organization?.address?.[0]?.line?.[0] || '',
      },
      labAccounts: initialLabAccounts,
      agreements: [],
      stripeKeys: { publishableKey: '', secretKey: '' },
      doseSpotClinic: { clinicId: '', clinicKey: '' },
      bridgeApiKey: '',
      bridgeWebhookSecret: '',
      stediApiKey: '',
    },
    validate: {
      name: (value) => (!value?.trim() ? 'Organization name is required' : null),
      type: (value) => (!value ? 'Organization type is required' : null),
      phone: (value) => {
        if (!value) return null
        const digits = value.replace(/\D/g, '')
        return digits.length === 10 ? null : 'Invalid phone number'
      },
      faxPhone: (value) => {
        if (!value) return null
        const digits = value.replace(/\D/g, '')
        return digits.length === 10 ? null : 'Invalid fax number'
      },
      billingNpi: (value) => {
        if (!value) return null
        return value.replace(/\D/g, '').length === 10 ? null : 'NPI must be 10 digits'
      },
      billingTaxId: (value) => {
        if (!value) return null
        return value.replace(/\D/g, '').length === 9 ? null : 'Tax ID (EIN) must be 9 digits'
      },
      address: {
        state: (value) => (!value?.trim() ? 'State is required' : null),
        zipCode: (value) => {
          if (!value?.trim()) return 'ZIP code is required'
          const digits = value.replace(/\D/g, '')
          if (!(digits.length === 5 || digits.length === 9)) {
            return 'ZIP code must be 5 or 9 digits'
          }
          return null
        },
        city: (value) => (!value?.trim() ? 'City is required' : null),
        address: (value) => (!value?.trim() ? 'Address is required' : null),
      },
      stripeKeys: {
        publishableKey: (value, values) => {
          const pub = value?.trim()
          const secret = values.stripeKeys.secretKey?.trim()
          if (!pub && !secret) return null
          if (!pub) return 'Publishable key is required when setting Stripe keys'
          return pub.startsWith('pk_') ? null : 'Expected a Stripe publishable key (pk_…)'
        },
        secretKey: (value, values) => {
          const secret = value?.trim()
          const pub = values.stripeKeys.publishableKey?.trim()
          if (!secret && !pub) return null
          if (!secret) return 'Secret key is required when setting Stripe keys'
          return /^(sk|rk)_/.test(secret) ? null : 'Expected a Stripe secret key (sk_…)'
        },
      },
      doseSpotClinic: {
        clinicId: (value, values) => {
          const clinicId = value?.trim()
          const clinicKey = values.doseSpotClinic.clinicKey?.trim()
          if (!clinicId && !clinicKey) return null
          if (!clinicId) return 'Clinic id is required when setting a DoseSpot clinic'
          return /^\d+$/.test(clinicId) ? null : 'Expected a numeric DoseSpot clinic id'
        },
        clinicKey: (value, values) => {
          const clinicKey = value?.trim()
          const clinicId = values.doseSpotClinic.clinicId?.trim()
          if (!clinicKey && !clinicId) return null
          return clinicKey ? null : 'Clinic key is required when setting a DoseSpot clinic'
        },
      },
      agreements: {
        insuranceCategory: (value) => (!value ? 'Select an insurance category' : null),
        insurancePlanType: (value) => (!value ? 'Select a plan type' : null),
        states: (_value, values, path) => {
          const index = Number(path.split('.')[1])
          const row = values.agreements[index]
          if (!row) return null
          if (!row.national && row.states.length === 0) {
            return 'Select at least one state or national coverage'
          }
          return null
        },
      },
    },
  })

  const addAlias = () => {
    form.insertListItem('aliases', '')
  }

  const removeAlias = (index: number) => {
    if (form.values.aliases.length > 1) {
      form.removeListItem('aliases', index)
    }
  }

  const handleSubmit = async (values: OrganizationFormData) => {
    let zip = values.address.zipCode || ''
    const digits = zip.replace(/\D/g, '')
    if (digits.length === 9) {
      zip = `${digits.slice(0, 5)}-${digits.slice(5)}`
    } else if (digits.length === 5) {
      zip = digits
    }
    form.setFieldValue('address.zipCode', zip)

    setIsLoading(true)

    try {
      // Filter out empty aliases
      const filteredAliases = values.aliases.filter((alias) => alias.trim())

      const existingTelecom =
        organization?.telecom?.filter((t) => t.system !== 'fax' && t.system !== 'phone') ?? []
      const phoneDigits = values.phone.replace(/\D/g, '')
      const faxDigits = values.faxPhone.replace(/\D/g, '')

      const organizationData: Organization = {
        resourceType: 'Organization',
        name: values.name,
        type: [
          {
            coding: [
              {
                system: 'http://terminology.hl7.org/CodeSystem/organization-type',
                code: values.type,
                display: ORGANIZATION_TYPES[values.type as keyof typeof ORGANIZATION_TYPES],
              },
            ],
          },
        ],
        alias: filteredAliases.length > 0 ? filteredAliases : undefined,
        telecom: [
          ...existingTelecom,
          ...(phoneDigits ? [{ system: 'phone' as const, value: values.phone }] : []),
          ...(faxDigits ? [{ system: 'fax' as const, value: values.faxPhone }] : []),
        ],
        address: [
          {
            state: values.address.state,
            country: 'US',
            postalCode: zip,
            city: values.address.city,
            line: [values.address.address],
          },
        ],
      }

      // Build identifier array: preserve identifiers this form does not own + re-add the
      // lab account and billing-identity values it edits. NPI/EIN are stored as bare
      // digits — the same entries Billing settings and the 837 pre-flight read.
      const existingNonLabIdentifiers = (organization?.identifier || []).filter(
        (id) =>
          !id.system?.startsWith('https://behuman.health/lab-account/') &&
          id.system !== NPI_IDENTIFIER_SYSTEM &&
          id.system !== EIN_IDENTIFIER_SYSTEM,
      )
      const labAccountIdentifiers = Object.entries(values.labAccounts)
        .filter(([, value]) => value.trim() !== '')
        .map(([key, value]) => ({
          system: LAB_ACCOUNT_SYSTEMS[key as keyof typeof LAB_ACCOUNT_SYSTEMS],
          value: value.trim(),
        }))
      const billingNpi = values.billingNpi.replace(/\D/g, '')
      const billingTaxId = values.billingTaxId.replace(/\D/g, '')
      const billingIdentifiers = [
        ...(billingNpi ? [{ system: NPI_IDENTIFIER_SYSTEM, value: billingNpi }] : []),
        ...(billingTaxId ? [{ system: EIN_IDENTIFIER_SYSTEM, value: billingTaxId }] : []),
      ]
      const allIdentifiers = [
        ...existingNonLabIdentifiers,
        ...labAccountIdentifiers,
        ...billingIdentifiers,
      ]
      if (allIdentifiers.length > 0) {
        organizationData.identifier = allIdentifiers
      }

      if (!organization) {
        const behumanProject = await medplum.searchOne('Project', 'name:contains=behuman')
        if (!behumanProject) {
          throw new Error('Failed to get behuman project')
        }
        organizationData.meta = {
          project: behumanProject.id,
        }
      } else if (organization && organization?.id) {
        organizationData.id = organization.id
        if (organization.meta?.project) {
          organizationData.meta = {
            ...organization.meta,
            project: organization.meta.project,
          }
        }
      }

      let result: Organization
      if (!organization) {
        result = await medplum.createResource(organizationData)
      } else {
        result = await medplum.updateResource(organizationData)
      }

      // Agreements reference the saved Organization, so they are created only
      // after the org write succeeds. Per-row failures don't roll back the org.
      const failedAgreements: string[] = []
      for (const row of values.agreements) {
        try {
          await middleware.createOrganizationAgreement({
            organizationId: result.id as string,
            insuranceCategory: row.insuranceCategory as InsuranceCategory,
            insurancePlanType: row.insurancePlanType as InsurancePlanType,
            national: row.national,
            states: row.states as USStateCode[],
          })
        } catch (error) {
          logError('super', 'Failed to create platform agreement', error)
          failedAgreements.push(row.insurancePlanType || 'agreement')
        }
      }

      // Stripe keys also reference the saved Organization; a failure here must
      // not read as a failed org save.
      let stripeKeysFailed = false
      const publishableKey = values.stripeKeys.publishableKey.trim()
      const secretKey = values.stripeKeys.secretKey.trim()
      if (publishableKey && secretKey) {
        try {
          await middleware.setOrganizationStripeKeys({
            organizationId: result.id as string,
            publishableKey,
            secretKey,
          })
        } catch (error) {
          logError('super', 'Failed to save organization Stripe keys', error)
          stripeKeysFailed = true
        }
      }

      // Same shape for the DoseSpot clinic override: it references the saved
      // Organization, and a rejected clinic key must not read as a failed org save.
      let doseSpotFailed = false
      const doseSpotClinicId = values.doseSpotClinic.clinicId.trim()
      const doseSpotClinicKey = values.doseSpotClinic.clinicKey.trim()
      if (doseSpotClinicId && doseSpotClinicKey) {
        try {
          await middleware.setOrganizationDoseSpotClinic({
            organizationId: result.id as string,
            clinicId: doseSpotClinicId,
            clinicKey: doseSpotClinicKey,
          })
        } catch (error) {
          logError('super', 'Failed to save organization DoseSpot clinic', error)
          doseSpotFailed = true
        }
      }

      // Same shape again for the Bridge key. The middleware validates it against Bridge
      // before storing, so a rejection here means the key is wrong — but the organization
      // itself is already saved and must not read as failed.
      let bridgeFailed = false
      const bridgeApiKey = values.bridgeApiKey.trim()
      const bridgeWebhookSecret = values.bridgeWebhookSecret.trim()
      if (bridgeApiKey) {
        try {
          await middleware.setOrganizationBridgeKey({
            organizationId: result.id as string,
            apiKey: bridgeApiKey,
            // Optional, and only ever sent alongside the API key: the write endpoint has no
            // separate call for it, so updating just the webhook secret means re-entering
            // the API key too.
            ...(bridgeWebhookSecret ? { webhookSecret: bridgeWebhookSecret } : {}),
          })
        } catch (error) {
          logError('super', 'Failed to save organization Bridge key', error)
          bridgeFailed = true
        }
      }

      // And once more for the Stedi key — validated against Stedi by the middleware, and
      // a rejection must not read as a failed org save either.
      let stediFailed = false
      const stediApiKey = values.stediApiKey.trim()
      if (stediApiKey) {
        try {
          await middleware.setOrganizationStediKey({
            organizationId: result.id as string,
            apiKey: stediApiKey,
          })
        } catch (error) {
          logError('super', 'Failed to save organization Stedi key', error)
          stediFailed = true
        }
      }

      if (stripeKeysFailed) {
        notifications.show({
          title: 'Stripe keys were not saved',
          message:
            'Organization saved, but Stripe rejected the keys or saving them failed. Re-open the organization and try again.',
          color: 'red',
        })
      }

      if (doseSpotFailed) {
        notifications.show({
          title: 'DoseSpot clinic was not saved',
          message:
            'Organization saved, but DoseSpot rejected the clinic id and key or saving them failed. Re-open the organization and try again.',
          color: 'red',
        })
      }

      if (bridgeFailed) {
        notifications.show({
          title: 'Bridge key was not saved',
          message:
            'Organization saved, but Bridge rejected the API key or saving it failed. Re-open the organization and try again.',
          color: 'red',
        })
      }

      if (stediFailed) {
        notifications.show({
          title: 'Stedi key was not saved',
          message:
            'Organization saved, but Stedi rejected the API key or saving it failed. Re-open the organization and try again.',
          color: 'red',
        })
      }

      if (failedAgreements.length > 0) {
        notifications.show({
          title: 'Some agreements were not saved',
          message: `Organization saved, but these agreements failed: ${failedAgreements.join(', ')}. Re-open the organization to retry.`,
          color: 'red',
        })
      } else if (!stripeKeysFailed && !doseSpotFailed && !bridgeFailed && !stediFailed) {
        notifications.show({
          title: 'Success',
          message: `Organization ${!organization ? 'created' : 'updated'} successfully`,
          color: 'green',
        })
      }

      onSave?.(result)
    } catch (error) {
      logError('super', `Error ${!organization ? 'creating' : 'updating'} organization`, error)
      notifications.show({
        title: 'Error',
        message: `Failed to ${!organization ? 'create' : 'update'} organization. Please try again.`,
        color: 'red',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const organizationTypeOptions = Object.entries(ORGANIZATION_TYPES).map(([code, display]) => ({
    value: code,
    label: display,
  }))

  return (
    <Card withBorder className="mx-auto max-w-2xl">
      <Title order={2} mb="md">
        {!organization ? 'Create Organization' : 'Update Organization'}
      </Title>

      <Alert icon={<IconInfoCircle size="1rem" />} title="Information" className="mb-4">
        {!organization
          ? 'Create a new organization with the specified details. All fields are required.'
          : 'Update the organization details.'}
      </Alert>

      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          {/* Organization Name */}
          <TextInput
            label="Organization Name"
            placeholder="Enter organization name"
            {...form.getInputProps('name')}
            required
          />

          {/* Organization Type */}
          <Select
            label="Organization Type"
            placeholder="Select organization type"
            data={organizationTypeOptions}
            {...form.getInputProps('type')}
            required
            searchable
          />

          {/* Address */}
          <Group grow>
            <TextInput
              label="Street Address"
              placeholder="Street address, unit, etc."
              {...form.getInputProps('address.address')}
              required
            />
            <TextInput
              label="City"
              placeholder="City"
              {...form.getInputProps('address.city')}
              required
            />
          </Group>
          <Group grow>
            <Select
              label="State"
              placeholder="Select state"
              data={states}
              {...form.getInputProps('address.state')}
              required
            />
            <InputBase
              label="ZIP Code"
              placeholder="00000 or 12345-1234"
              component={IMaskInput}
              mask={[{ mask: '00000' }, { mask: '00000-0000' }]}
              {...form.getInputProps('address.zipCode')}
              required
            />
          </Group>

          {/* Aliases */}
          <div>
            <Group justify="space-between" mb="xs">
              <Text size="sm" fw={500}>
                Aliases
              </Text>
              <ActionIcon variant="light" onClick={addAlias} size="sm">
                <IconPlus size={16} />
              </ActionIcon>
            </Group>
            <Stack gap="xs">
              {form.values.aliases.map((_, index) => (
                <Group key={index}>
                  <TextInput
                    placeholder="Enter alias"
                    {...form.getInputProps(`aliases.${index}`)}
                    style={{ flex: 1 }}
                  />
                  {form.values.aliases.length > 1 && (
                    <ActionIcon
                      variant="light"
                      color="red"
                      onClick={() => removeAlias(index)}
                      size="sm"
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  )}
                </Group>
              ))}
            </Stack>
          </div>

          {/* Phone & Fax */}
          <Group grow>
            <InputBase
              label="Phone"
              placeholder="(000) 000-0000"
              component={IMaskInput}
              mask="(000) 000-0000"
              value={form.values.phone}
              onAccept={(value) => form.setFieldValue('phone', value)}
              error={form.errors.phone}
            />
            <InputBase
              label="Fax"
              placeholder="(000) 000-0000"
              component={IMaskInput}
              mask="(000) 000-0000"
              value={form.values.faxPhone}
              onAccept={(value) => form.setFieldValue('faxPhone', value)}
              error={form.errors.faxPhone}
            />
          </Group>

          {/* Lab Account Numbers (only for clinic/provider orgs) */}
          {code === 'prov' && (
            <>
              <Divider my="sm" />
              <Title order={4}>Billing provider</Title>
              <Text size="xs" c="dimmed">
                The claim billing identity (837 billing-provider block) — a claim cannot leave for a
                clearinghouse without it.
              </Text>
              <Group grow>
                <TextInput
                  label="NPI"
                  placeholder="10 digits"
                  {...form.getInputProps('billingNpi')}
                />
                <TextInput
                  label="Tax ID (EIN)"
                  placeholder="9 digits"
                  {...form.getInputProps('billingTaxId')}
                />
              </Group>

              <Divider my="sm" />
              <Title order={4}>Lab Account Numbers</Title>
              {Object.entries(LAB_ACCOUNT_LABELS).map(([key, label]) => (
                <TextInput
                  key={key}
                  label={label}
                  placeholder={`Enter ${label} account number`}
                  {...form.getInputProps(`labAccounts.${key}`)}
                />
              ))}

              <PlatformAgreements
                organizationId={organization?.id}
                form={form as unknown as AgreementsHostForm}
              />

              <OrganizationStripeSection
                organizationId={organization?.id}
                form={form as unknown as StripeHostForm}
              />

              <OrganizationDoseSpotSection
                organizationId={organization?.id}
                form={form as unknown as DoseSpotHostForm}
              />

              <OrganizationBridgeSection
                organizationId={organization?.id}
                form={form as unknown as BridgeHostForm}
              />

              <OrganizationStediSection
                organizationId={organization?.id}
                form={form as unknown as StediHostForm}
              />
            </>
          )}

          {/* Action Buttons */}
          <Group justify="flex-end" mt="xl">
            {onCancel && (
              <Button variant="subtle" onClick={onCancel}>
                Cancel
              </Button>
            )}
            <Button type="submit" loading={isLoading}>
              {!organization ? 'Create Organization' : 'Update Organization'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Card>
  )
}
